import { AxiosResponse } from "axios";
import Environment from "../../utils/Environement";
import BaseApi from "../base/BaseApi";

interface RequestHeader {
    Authorization: string;
    'Content-Type': string
    Accept: string
}

export interface ObyPayUser {
    id: string
    identification: string
    name: string
    token: string
    signatureKey: string
}

export interface ObyPayMaster {
    id: string
    name: string
}

export interface paymentMapping {
    pos: Record<string, string>
    online: Record<string, string>
}

export interface Listener {
    id: string
    events: string[]
    url: string
}

type ObyPayAuthentication = {
    master?: ObyPayMaster,
    location?: ObyPayUser
}


export default class ObyPayApi extends BaseApi {
    public readonly isDev: boolean
    public readonly isPopina: boolean

    constructor(isDev: boolean, isPopina: boolean) {
        super(
            isDev ? "https://public-api.obypay.net/": "https://public-api-obypay.obypay.com/", false
        );
        this.isDev = isDev;
        this.isPopina = isPopina;
    }

    private header = (token: string): RequestHeader => {
        return {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json"
        }
    }

    private handleError(error: unknown): string | unknown {
        if (error instanceof Error) {
            return error.message
        }
        return error
    }

    async authenticateObyPayUser(token: string): Promise<ObyPayUser | ObyPayMaster> {
        try {
            const authentication: AxiosResponse<{ data: ObyPayAuthentication }> = await this.getRequest("api/v1/me", this.header(token));
            const data: ObyPayAuthentication = authentication.data.data
            const isMaster = 'master' in data
            const locationTypeMap = {
                master: data.master,
                location: data.location
            }

            const result = locationTypeMap[isMaster ? 'master' : 'location']

            if (!result) {
                throw new Error("Authentication data object is missing");
            }

            return result
            
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async createOrderListener(token: string, userUid: string): Promise<string> {
        const isPopina = Environment.isPopina
        const isDev = Environment.isDev

        let url: string;

        if (isPopina) {
            if (isDev) {
                url = `https://us-central1-popina-dev.cloudfunctions.net/api/v1/orders/${userUid}`
            } else {
                url = `https://us-central1-popina-prod.cloudfunctions.net/api/v1/orders/${userUid}`
            }
        }
        else {
            if (isDev) {
                url = `https://us-central1-jalia-dev.cloudfunctions.net/api/v1/orders/${userUid}`
            } else {
                url = `https://us-central1-jalia-prod.cloudfunctions.net/api/v1/orders/${userUid}`
            }
        }

        const body = {
            events: [
                "order.created",
                "order.canceled"
            ],
            url: url
        }
        try {
            const listener = await this.postRequest("api/v1/notifications/listeners", this.header(token), body);
            return listener.data.data.id as string
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async getAllListeners(token: string): Promise<Listener[]> {
        try {
            const listeners = await this.getRequest("api/v1/notifications/listeners", this.header(token));
            return listeners.data.data.data as Listener[]
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async deleteOrderListener(token: string, listenerId: string): Promise<void> {
        try {
            if (listenerId === undefined) throw new Error("listenerId is undefined")
            await this.deleteRequest(`api/v1/notifications/listeners/${listenerId}`, this.header(token))
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async mapObypayPaiementWithPOSPaiement(token: string, mapping: paymentMapping): Promise<void> {
        try {
            await this.postRequest('api/v1/payments/instruments', this.header(token), mapping)
        } catch (error) {
            throw this.handleError(error)
        }
    }
}